import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, X, Info } from "lucide-react";
import axios from "axios";
import { useIpadLive } from "@/lib/useIpadLive";
import { trackAction } from "@/lib/analyticsTracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Discreet post-session rating prompt for guests.
 *
 * Fetches pending sessions for this guest on mount. When there's at least one,
 * renders a soft card at the top of the guest page: "How was your <treatment>?".
 * The default is anonymous — ratings become visible to staff only after the
 * charter completes, unless the guest opts to publish immediately.
 *
 * Usage: `<GuestRatingPrompt guestId={guestId} />` — self-contained, silent when
 * there are no pending sessions.
 */
export default function GuestRatingPrompt({ guestId }) {
  const [pending, setPending] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [note, setNote] = useState("");
  const [revealNow, setRevealNow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const load = useCallback(async () => {
    if (!guestId) return;
    try {
      const { data } = await axios.get(`${API}/public/ratings/pending`, { params: { guest_id: guestId } });
      // Backend already excludes cancelled bookings, but we also defensively
      // filter here so a stale response (or a booking cancelled after the
      // prompt loaded) can never surface a rate-later card for something
      // the guest already scrapped. `status === "cancelled"` shouldn't
      // appear here — belt-and-braces.
      const items = (data.items || []).filter((i) => (i?.status || "").toLowerCase() !== "cancelled");
      setPending(items);
    } catch { /* silent — never break the booking flow because of a rating prompt */ }
  }, [guestId]);
  useEffect(() => { load(); }, [load]);

  // Re-fetch the pending list on any booking lifecycle event so a fresh
  // cancellation (initiated from the guest's iPad, another cabin device,
  // or the Spa Ops kiosk) instantly hides the rate-later card for that
  // booking. Without this the prompt used to keep asking the guest to
  // rate a booking they had just cancelled.
  useIpadLive((ev) => {
    const t = ev?.type || "";
    if (t.startsWith("booking.")) load();
  });

  // Fire once per unique booking so the "Rating engagement" KPI's
  // denominator (prompts shown) is meaningful. Hook MUST live above
  // any early return to satisfy the Rules of Hooks — otherwise CRA's
  // production build refuses to compile.
  const shownRef = React.useRef(new Set());
  const currentId = pending[0]?.id || null;
  useEffect(() => {
    if (currentId && !shownRef.current.has(currentId)) {
      shownRef.current.add(currentId);
      trackAction("rating", "rating_prompt_shown", { booking_id: currentId });
    }
  }, [currentId]);

  if (dismissed || pending.length === 0) return null;

  const current = pending[0];

  const submit = async () => {
    if (!stars) return toast.error("Tap a star to rate");
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/ratings`, {
        booking_id: current.id, guest_id: guestId, stars,
        reveal_now: revealNow, note: note.trim() || null,
      });
      trackAction("rating", "rating_submitted", { booking_id: current.id, stars, reveal_now: !!revealNow });
      toast.success(revealNow ? "Thanks — your rating is public" : "Thanks — your rating is anonymous until the charter ends");
      // Advance to next pending session (or hide widget if none left)
      setStars(0); setHover(0); setNote(""); setRevealNow(false);
      setPending((prev) => prev.slice(1));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your rating");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mb-6 border border-accent/40 bg-accent/5 p-5 shadow-sm" data-testid="guest-rating-prompt">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="overline text-accent text-[10px] flex items-center gap-1.5">
            <Star className="h-3 w-3" /> How was your last session?
          </div>
          <div className="font-display text-lg mt-1">{current.treatment_name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {new Date(current.start_at).toLocaleString()}
            {pending.length > 1 && <span className="ml-2 text-accent">· {pending.length - 1} more pending</span>}
          </div>
        </div>
        <button onClick={() => setDismissed(true)}
                className="text-muted-foreground hover:text-foreground p-1"
                data-testid="rating-dismiss"
                aria-label="Not now">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Star row */}
      <div className="flex items-center gap-1 my-3" data-testid="rating-stars"
           onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n}
                  type="button"
                  onClick={() => setStars(n)}
                  onMouseEnter={() => setHover(n)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`rating-star-${n}`}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}>
            <Star className={`h-7 w-7 transition-colors ${
              (hover || stars) >= n ? "fill-accent text-accent" : "text-muted-foreground/40"
            }`} />
          </button>
        ))}
        {stars > 0 && <span className="ml-2 text-sm font-mono text-accent">{stars} / 5</span>}
      </div>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional comment (kept private, shared only with staff)"
        maxLength={400}
        rows={2}
        className="w-full text-xs border border-border p-2 bg-background focus:outline-none focus:border-accent"
        data-testid="rating-note"
      />

      <div className="flex items-start gap-2 mt-3 text-[11px] text-muted-foreground">
        <input type="checkbox" id="reveal-now"
               checked={revealNow}
               onChange={(e) => setRevealNow(e.target.checked)}
               className="mt-0.5 accent-accent"
               data-testid="rating-reveal-now" />
        <label htmlFor="reveal-now" className="cursor-pointer">
          Show my rating to staff now.
          <button type="button" onClick={() => setShowInfo((v) => !v)} className="ml-1 text-accent inline-flex items-center gap-0.5" aria-label="Info">
            <Info className="h-3 w-3" />
          </button>
        </label>
      </div>
      {showInfo && (
        <p className="text-[11px] text-muted-foreground mt-2 pl-6" data-testid="rating-info">
          By default, ratings stay <strong>anonymous</strong> and are only revealed to staff after your
          charter ends — so you can be candid without any awkwardness during the trip. Tick the box above
          to publish this rating to staff now (still anonymous unless you write your name in the comment).
        </p>
      )}

      <div className="flex items-center gap-2 mt-4">
        <button onClick={submit} disabled={submitting || !stars}
                className="text-sm px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 disabled:opacity-40"
                data-testid="rating-submit">
          {submitting ? "Sending…" : "Submit rating"}
        </button>
        <button onClick={() => setPending((prev) => prev.slice(1).concat(prev.slice(0, 1)))}
                className="text-xs text-muted-foreground hover:text-foreground px-3 py-1"
                data-testid="rating-skip"
                disabled={pending.length < 2}>
          Skip for now
        </button>
      </div>
    </div>
  );
}
